import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import {
  readingKPI,
  questionThemes,
  students,
  cohortStats,
  type QuestionTheme,
} from '../data/mockAnalyticsData';

// ─── KPI Card Component ──────────────────────────────────────────────────────

const KPICard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, icon, children, className = '' }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`rounded-2xl border p-6 ${
      isDarkMode
        ? 'bg-navy-800/80 border-white/10 backdrop-blur-sm'
        : 'bg-white border-gray-200 shadow-sm'
    } ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <span className={`${isDarkMode ? 'text-frost-400' : 'text-frost-600'}`}>{icon}</span>
        <h3 className={`text-sm font-medium uppercase tracking-wider ${
          isDarkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>{title}</h3>
      </div>
      {children}
    </div>
  );
};

// ─── Question Theme Card ─────────────────────────────────────────────────────

const QuestionThemeCard: React.FC<{ theme: QuestionTheme }> = ({ theme }) => {
  const { isDarkMode } = useTheme();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-xl border p-4 transition-all cursor-pointer ${
      isDarkMode
        ? 'bg-navy-700/50 border-white/5 hover:border-frost-500/30'
        : 'bg-gray-50 border-gray-200 hover:border-frost-400'
    }`} onClick={() => setExpanded(!expanded)}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {theme.concept}
            </h4>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isDarkMode ? 'bg-frost-500/20 text-frost-300' : 'bg-frost-100 text-frost-700'
            }`}>
              {theme.studentCount} students
            </span>
          </div>
          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {theme.chapter} &middot; {theme.totalQuestions} questions
          </p>
        </div>
        {expanded ? (
          <ChevronUpIcon className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        ) : (
          <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        )}
      </div>

      {/* Always show first question */}
      <div className={`mt-2 text-sm italic pl-3 border-l-2 ${
        isDarkMode
          ? 'text-gray-300 border-frost-500/40'
          : 'text-gray-600 border-frost-400'
      }`}>
        "{theme.sampleQuestions[0]}"
      </div>

      {expanded && theme.sampleQuestions.length > 1 && (
        <div className="mt-2 space-y-2">
          {theme.sampleQuestions.slice(1).map((q, i) => (
            <div key={i} className={`text-sm italic pl-3 border-l-2 ${
              isDarkMode
                ? 'text-gray-300 border-frost-500/40'
                : 'text-gray-600 border-frost-400'
            }`}>
              "{q}"
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Analytics Page ─────────────────────────────────────────────────────

const Analytics: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { instituteId } = useParams<{ instituteId: string }>();
  const [showLeastRead, setShowLeastRead] = useState(false);
  const [sortField, setSortField] = useState<'pagesRead' | 'readingHours' | 'questionsAsked' | 'lastActiveDate'>('pagesRead');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');

  const sortedStudents = [...students]
    .filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon: React.FC<{ field: typeof sortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ArrowUpIcon className="w-3 h-3 inline ml-1" />
      : <ArrowDownIcon className="w-3 h-3 inline ml-1" />;
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-navy-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`border-b ${isDarkMode ? 'border-white/10 bg-navy-800/50' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">{cohortStats.name}</h1>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {cohortStats.subject} &middot; {cohortStats.totalStudents} students &middot; Report: April 8, 2026
              </p>
            </div>
            <div className="relative">
              <select
                className={`appearance-none pl-9 pr-8 py-2 rounded-xl text-sm font-medium cursor-pointer outline-none ${
                  isDarkMode
                    ? 'bg-navy-700 border border-white/10 text-white'
                    : 'bg-gray-100 border border-gray-200 text-gray-900'
                }`}
                defaultValue="ncert-physics"
              >
                <option value="ncert-physics">NCERT Physics Part I & II</option>
                <option value="ncert-chemistry" disabled>NCERT Chemistry (coming soon)</option>
                <option value="ncert-biology" disabled>NCERT Biology (coming soon)</option>
              </select>
              <BookOpenIcon className="w-4 h-4 text-frost-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <ChevronDownIcon className={`w-3 h-3 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* ─── KPI Cards ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Card 1: Reading Engagement */}
          <KPICard title="Reading Engagement" icon={<BookOpenIcon className="w-5 h-5" />}>
            <div className="flex items-baseline gap-3 mb-1">
              <span className="text-4xl font-bold">{readingKPI.readYesterday}</span>
              <span className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                / {readingKPI.totalStudents} read yesterday
              </span>
            </div>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Avg <span className="font-semibold text-frost-400">{readingKPI.avgPagesPerStudent} pages</span> per student
            </p>

            {/* Chapter coverage */}
            <div className="mb-4">
              <p className={`text-xs font-medium uppercase tracking-wider mb-2 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>Chapters Being Read</p>
              <div className="space-y-1.5">
                {readingKPI.chaptersActive.map((ch, i) => (
                  <div key={i} className={`flex justify-between text-sm px-3 py-1.5 rounded-lg ${
                    isDarkMode ? 'bg-navy-700/30' : 'bg-gray-50'
                  }`}>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ch.chapter}</span>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                      {ch.studentsReading} students, avg {ch.avgPages} pg
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Least read tab */}
            <button
              onClick={() => setShowLeastRead(!showLeastRead)}
              className={`text-xs font-medium flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                showLeastRead
                  ? isDarkMode ? 'bg-frost-500/20 text-frost-300' : 'bg-frost-100 text-frost-700'
                  : isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Students with least pages read
              {showLeastRead ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
            </button>

            {showLeastRead && (
              <div className="mt-3 space-y-2">
                {readingKPI.leastReadStudents.map((s, i) => (
                  <div key={i} className={`flex justify-between text-sm px-3 py-2 rounded-lg ${
                    isDarkMode ? 'bg-navy-700/50' : 'bg-gray-100'
                  }`}>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{s.name}</span>
                    <div className="flex gap-4">
                      <span className={`font-medium ${s.pages === 0 ? 'text-red-400' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {s.pages} pages
                      </span>
                      <span className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{s.lastActive}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </KPICard>

          {/* Card 2: Common Question Themes */}
          <KPICard title="Common Themes of Questions — Yesterday" icon={<ChatBubbleLeftRightIcon className="w-5 h-5" />}>
            <div className="space-y-3">
              {questionThemes.map((theme, i) => (
                <QuestionThemeCard key={i} theme={theme} />
              ))}
            </div>
          </KPICard>
        </div>

        {/* ─── Student List ──────────────────────────────────────────── */}
        <div className={`rounded-2xl border overflow-hidden ${
          isDarkMode ? 'bg-navy-800/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserGroupIcon className={`w-5 h-5 ${isDarkMode ? 'text-frost-400' : 'text-frost-600'}`} />
              <h3 className={`text-sm font-medium uppercase tracking-wider ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>All Students</h3>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
              isDarkMode ? 'bg-navy-700 border border-white/10' : 'bg-gray-100 border border-gray-200'
            }`}>
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search students..."
                className={`bg-transparent text-sm outline-none w-48 ${
                  isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-xs uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-500 border-t border-white/5' : 'text-gray-400 border-t border-gray-100'
                }`}>
                  <th className="text-left px-6 py-3 font-medium">Student</th>
                  <th className="text-right px-6 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort('pagesRead')}>
                    Pages Read <SortIcon field="pagesRead" />
                  </th>
                  <th className="text-right px-6 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort('readingHours')}>
                    Hours <SortIcon field="readingHours" />
                  </th>
                  <th className="text-right px-6 py-3 font-medium cursor-pointer select-none" onClick={() => handleSort('questionsAsked')}>
                    Questions <SortIcon field="questionsAsked" />
                  </th>
                  <th className="text-right px-6 py-3 font-medium">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {sortedStudents.map((student) => (
                  <tr
                    key={student.id}
                    onClick={() => navigate(`/analytics/${instituteId}/student/${student.id}`)}
                    className={`cursor-pointer transition-colors ${
                      isDarkMode
                        ? 'hover:bg-navy-700/50 border-t border-white/5'
                        : 'hover:bg-gray-50 border-t border-gray-100'
                    }`}
                  >
                    <td className="px-6 py-3">
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {student.name}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {student.pagesRead}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {student.readingHours}h
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {student.questionsAsked}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {student.lastActive}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
